import { firestore } from "firebase/app";
import { DateTime, Duration } from "luxon";

export function parseDate(value: string) {
  return DateTime.fromISO(value)?.toJSDate();
}

export function parseReportDate(dateString: string) {
  return DateTime.fromFormat(dateString, "yyMMddHHmm").toJSDate();
}

export function toStartOfDay(timestamp: firestore.Timestamp) {
  const date = DateTime.fromJSDate(timestamp.toDate())
    .startOf("day")
    .toJSDate();

  return firestore.Timestamp.fromDate(date);
}

export function adjustEndDate(timestamp?: firestore.Timestamp | null) {
  if (timestamp) {
    return timestamp;
  } else {
    return firestore.Timestamp.fromMillis(253370764800);
  }
}

export function toEndOfDay(timestamp: firestore.Timestamp) {
  const date = DateTime.fromJSDate(timestamp.toDate())
    .endOf("day")
    .toJSDate();

  return firestore.Timestamp.fromDate(date);
}

export function getMinutesSince(timestamp: firestore.Timestamp) {
  return Math.floor(
    (new Date().getTime() - timestamp.toDate().getTime()) / (1000 * 60)
  );
}

export function isAfter(
  timestamp: firestore.Timestamp,
  reference: firestore.Timestamp
) {
  const d1 = timestamp.toDate();
  const referenceJsDate = reference.toDate();

  const d1Date = d1.getDate();
  const d1Month = d1.getMonth();
  const d1Year = d1.getFullYear();

  const referenceDate = referenceJsDate.getDate();
  const referenceMonth = referenceJsDate.getMonth();
  const referenceYear = referenceJsDate.getFullYear();

  if (referenceYear > d1Year) {
    return false;
  }

  if (referenceYear < d1Year) {
    return true;
  }

  if (referenceMonth > d1Month) {
    return false;
  }

  if (referenceMonth < d1Month) {
    return true;
  }

  if (referenceDate > d1Date) {
    return false;
  }

  if (referenceDate < d1Date) {
    return true;
  }

  return false;
}

export function areDatesOnSameDay(d1: Date, d2: Date) {
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
}

export function areOnSameDay(
  t1?: firestore.Timestamp | null,
  t2?: firestore.Timestamp | null,
  alreadyInDateFormat?: boolean
) {
  if (!t1 || !t2) {
    return false;
  }

  const d1 = t1.toDate();
  const d2 = alreadyInDateFormat ? t2 : t2.toDate();

  return areDatesOnSameDay(d1, d2 as Date);
}

export function findDaylightSavings(start: DateTime, end: DateTime) {
  const endOffset = end.offset;
  const endMillis = end.toMillis();

  if (start.offset === endOffset) {
    return null;
  }

  const oneDay = Duration.fromObject({ day: 1 });

  let next = start.plus(oneDay);

  while (next.offset !== endOffset) {
    if (next.toMillis() > endMillis) {
      break;
    }

    next = next.plus(oneDay);
  }

  next = next.startOf("day");

  const oneHour = Duration.fromObject({ hour: 1 });

  while (next.offset !== endOffset) {
    next = next.plus(oneHour);
  }

  return next;
}

export function getNumberDaysBetween(
  date1: Date,
  date2: Date,
  round?: boolean
) {
  const timeDiffInDays = Math.abs(date1.getTime() - date2.getTime()) / 86400000;

  return round ? Math.round(timeDiffInDays) : Math.ceil(timeDiffInDays);
}

/**
 * Returns the date `minutes` real minutes after `date`.
 *
 * Use this to turn a device's "minutes since run start" into a date. `setMinutes(getMinutes() + n)`
 * counts on the local clock instead, which lands an hour off once a time change lies in between
 * (2026-10-25: BB280's pause band drawn an hour after the pause).
 */
export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

/**
 * Moves `date` forward by `minutes` real minutes, in place. The in-place twin of {@link addMinutes}
 * for loops that advance one date sample by sample.
 */
export function advanceByMinutes(date: Date, minutes: number) {
  date.setTime(date.getTime() + minutes * 60 * 1000);
}

export function getNumberMinutesBetween(date1: Date, date2: Date) {
  return Math.ceil(Math.abs(date1.getTime() - date2.getTime()) / (60 * 1000));
}

export function roundTimestampToMinutes(timestamp: number) {
  return timestamp - (timestamp % (60 * 1000));
}

export function formatDateToHoursAndMinuteString(date: Date) {
  return DateTime.fromJSDate(date).toFormat("HH:mm")
}

export function formatDateToYearMonthDateString(date: Date) {
  return DateTime.fromJSDate(date).toFormat("yyyy.MM.dd")
}

export function formatDateTohDateMonthYearString(date: Date) {
  return DateTime.fromJSDate(date).toFormat("dd.MM.yyyy")
}

export function formatDateToDayMonthYear(date: Date | null): string | null{
  if (!date) {
    return null;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
}

export function parseDateFromDayMonthYear(dateStr: string | null): firestore.Timestamp | null {
  if (!dateStr || dateStr.length < 5) {
    return null;
  }
  let separator = "."
  
  if(dateStr.includes("/")){
    separator = "/"
  }
  if(dateStr.includes("-")) {
    separator = "-"
  }

  const parts = dateStr.split(separator);
  let day, month, year;

  if (parts.length === 2) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = new Date().getFullYear();
  } else if (parts.length === 3) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2]);
  } else {
    return null;
  }

  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    return null;
  }

  return firestore.Timestamp.fromDate(new Date(`${month}/${day}/${year}`));
}

export function getDateFromYearMonthDateString(value: string | undefined | null): Date | undefined {
  if (!value) {
    return undefined
  }

  const parts = value.split(".").map(token => Number(token)).filter(n => !isNaN(n))
  
  if (parts.length != 3) {
    return undefined
  }

  return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0)
}

export function getDateFromHoursAndMinuteString(value: string | undefined | null): Date | undefined {
  if (!value) {
    return undefined
  }

  const parts = value.split(":").map(token => Number(token)).filter(n => !isNaN(n))

  if (parts.length != 2) {
    return undefined
  }

  const date = new Date()

  date.setHours(parts[0])
  date.setMinutes(parts[1])
  date.setSeconds(0)

  return date
}

export function getTimestampForHourAndMinutes(data: { hour: number; minute: number }) {
  const { hour, minute } = data;

  return firestore.Timestamp.fromDate(
    DateTime.fromJSDate(new Date())
    .set({
      hour: hour,
      minute: minute,
      second: 0,
      millisecond: 0,
    })
    .toJSDate()
  );
}

export function getHoursAndMinutesFromTimestamp(timetamp: firestore.Timestamp) {
  const dateTime = DateTime.fromJSDate(timetamp.toDate());

  return {
    hour: dateTime.hour,
    minute: dateTime.minute,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function timestampToDateParser(timestamp: any): Date | null {
  // Helper function to validate if the object is a valid date
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValidDate = (d: any): boolean => {
    return d instanceof Date && !isNaN(d.getTime());
  };

  if (!timestamp) {
    return null;
  }

  try {
    if (timestamp instanceof Date && isValidDate(timestamp)) {
      return timestamp;
    }

    if (timestamp instanceof firestore.Timestamp) {
      return timestamp.toDate();
    }

    if (typeof timestamp === 'string') {
      const jsDate = new Date(timestamp);
      if (isValidDate(jsDate)) {
        return jsDate;
      }
    } else if (typeof timestamp === 'object' && ('seconds' in timestamp || '_seconds' in timestamp)) {
      // Handle objects that look like Firestore timestamps
      const seconds = timestamp._seconds || timestamp.seconds || 0;
      const nanoseconds = timestamp._nanoseconds || timestamp.nanoseconds || 0;
      const convertedDate = new Date(seconds * 1000 + nanoseconds / 1000000);
      if (isValidDate(convertedDate)) {
        return convertedDate;
      }
    }
  } catch (e) {
    // [SKIP] Error
  }

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function dateToTimestampParser(date: any) {
  if (!date) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValidDate = (d: any): boolean => {
    return d instanceof Date && !isNaN(d.getTime());
  };

  try {
    const jsDate = new Date(date);

    if (isValidDate(jsDate)) {
      return firestore.Timestamp.fromDate(jsDate);
    }

    const seconds = date._seconds || date.seconds;
    const nanoseconds = date._nanoseconds || date.nanoseconds;

    if (seconds && nanoseconds) {
      const convertedDate = new Date(seconds * 1000 + nanoseconds / 1000000);
      if (isValidDate(convertedDate)) {
        return firestore.Timestamp.fromDate(convertedDate);
      }
    }
  } catch(e) {
    // [SKIP] Error
  }

  return null;
}

export function parseFirestoreTimestampString(dateStr: string): Date | null {
  // Match the date and time parts (ignoring the weekday and time zone name)
  const regex = /(\w+), (\w+) (\d{1,2}), (\d{4}) (\d{1,2}):(\d{2}):(\d{2}) (\w+)/;
  const match = dateStr.match(regex);

  if (!match) {
    return null;
  }

  // Extract relevant parts from the match
  const [, , month, day, year, hours, minutes, seconds, period] = match;

  // Convert month string to a number
  const monthNames: { [key: string]: number } = {
    January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
    July: 6, August: 7, September: 8, October: 9, November: 10, December: 11
  };
  
  const monthIndex = monthNames[month];

  if (monthIndex === undefined) {
    return null; // Invalid month
  }

  // Adjust for 12-hour AM/PM format
  let hour = parseInt(hours, 10);
  if (period === "PM" && hour !== 12) {
    hour += 12;
  } else if (period === "AM" && hour === 12) {
    hour = 0;
  }

  // Construct the date object in UTC (adjust time zone as needed)
  const parsedDate = new Date(Date.UTC(
    parseInt(year, 10),
    monthIndex,
    parseInt(day, 10),
    hour,
    parseInt(minutes, 10),
    parseInt(seconds, 10)
  ));

  // Adjust manually for the Central European Summer Time (UTC+2)
  parsedDate.setUTCMinutes(parsedDate.getUTCMinutes() - 120);

  return isNaN(parsedDate.getTime()) ? null : parsedDate;
}

/**
 * Formats a UTC timestamp as "HH:MM" (24h) in the given IANA timezone.
 * Normalises the edge case where some runtimes emit "24:xx" for midnight.
 *
 * @param ms - UTC timestamp in milliseconds.
 * @param timezone - IANA timezone identifier (e.g. "Europe/Berlin").
 * @returns A "HH:MM" string in the client's timezone.
 */
export function formatTimestampAsHHMM(ms: number, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${hour === "24" ? "00" : hour}:${minute}`;
}

/**
 * Parses an "HH:MM" string into total minutes since midnight.
 * Returns null when the input is absent or not a valid 24h time.
 *
 * @param hhMM - A string in "HH:MM" or "H:MM" format.
 * @returns Total minutes since midnight, or null on parse failure.
 */
export function parseHHMMToMinutes(hhMM: string): number | null {
  if (!hhMM || !/^\d{1,2}:\d{2}$/.test(hhMM)) return null;
  const [hh, mm] = hhMM.split(":").map(Number);
  if (isNaN(hh) || isNaN(mm) || hh > 23 || mm > 59) return null;
  return hh * 60 + mm;
}